import React from 'react';
import { Smartphone, Monitor, LineChart, Users, TrendingUp, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';

const Products = () => {
  return (
    <div className="min-h-screen pt-32 md:pt-36">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#B8FF3B] px-4 py-2 rounded-full mb-6">
              <span className="text-[#1B4332] font-semibold text-sm">Product Innovations</span>
            </div>
            <h1 className="display-large mb-6">Innovative Fitness Products</h1>
            <p className="body-large mb-8">
              Proprietary technology solutions designed to revolutionize fitness and recreation operations. 
              Developed through 20+ years of industry expertise.
            </p>
            <Link to="/contact" className="btn-primary">
              Request Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Our Product Line</h2>
            <p className="body-large">Cutting-edge solutions for modern fitness businesses</p>
          </div>

          <div className="network-grid">
            {mockData.products.map((product, index) => {
              const icons = [Smartphone, LineChart, Monitor, Users];
              const IconComponent = icons[index % icons.length];
              return (
                <div key={index} className="network-card">
                  <div className="bg-[#B8FF3B] w-16 h-16 rounded-2xl flex items-center justify-center mb-4">
                    <IconComponent size={32} className="text-[#1B4332]" />
                  </div>
                  <h3 className="network-card-title">{product}</h3>
                  <p className="network-card-content mb-4">
                    Professional-grade {product.toLowerCase()} built for scalability and performance.
                  </p>
                  <button className="btn-secondary text-sm py-2">
                    View Details
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recreation Technology Section */}
      <section className="py-16 md:py-24 bg-[#E8F0EE]">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Recreation Technology Solutions</h2>
            <p className="body-large">Comprehensive technology for modern recreation management</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {mockData.recreationSolutions.map((solution, index) => {
              const icons = [Users, Monitor, TrendingUp];
              const IconComponent = icons[index % icons.length];
              return (
                <div key={index} className="network-card">
                  <div className="bg-[#0EA5E9] w-16 h-16 rounded-2xl flex items-center justify-center mb-4">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="network-card-title">{solution}</h3>
                  <p className="network-card-content">
                    Advanced {solution.toLowerCase()} designed to streamline your recreation operations.
                  </p>
                </div>
              );
            })}
          </div>

          {/* Recreation Benefits */}
          <div className="max-w-4xl mx-auto">
            <h3 className="heading-2 text-center mb-8">Recreation Technology Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Increased Efficiency',
                  description: 'Automate routine tasks and free staff to focus on participant experience.'
                },
                {
                  title: 'Better Sustainability',
                  description: 'Digital screens and paperless systems reduce environmental impact.'
                },
                {
                  title: 'Enhanced Training',
                  description: 'Learning Management Systems ensure consistent, high-quality training for staff.'
                },
                {
                  title: 'Real-Time Data',
                  description: 'Make informed decisions with instant access to participation rates and performance.'
                }
              ].map((benefit, index) => (
                <div key={index} className="network-card">
                  <h4 className="heading-3 mb-3">{benefit.title}</h4>
                  <p className="body-medium">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Product Features */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Key Features</h2>
            <p className="body-large">What makes FITNOVEXA products stand out</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Cloud-Based', description: 'Access your data anywhere, anytime with secure cloud infrastructure' },
              { title: 'Mobile-First', description: 'Optimized for mobile devices to support on-the-go management' },
              { title: 'Analytics Dashboard', description: 'Comprehensive insights and reporting for data-driven decisions' },
              { title: 'Easy Integration', description: 'Seamlessly connect with existing systems and third-party tools' },
              { title: 'Scalable', description: 'Grows with your business from startup to enterprise' },
              { title: 'Expert Support', description: '24/7 customer support from fitness technology specialists' }
            ].map((feature, index) => (
              <div key={index} className="network-card text-center">
                <h3 className="heading-3 mb-3">{feature.title}</h3>
                <p className="body-medium">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="bg-[#E8F0EE] rounded-[32px] p-8 md:p-16 text-center max-w-4xl mx-auto">
            <h2 className="heading-1 mb-6">Ready to Modernize Your Operations?</h2>
            <p className="body-large mb-8">
              Contact us for a personalized product demonstration and pricing information tailored to your needs.
            </p>
            <Link to="/contact" className="btn-primary">
              Get Started
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;